// =============================================================================
// MeuDriver — Batch P4: AdminAuditLog
// Tabela paginada de ações administrativas com filtros e expandir JSON.
// =============================================================================

import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ClipboardList, Search, X } from 'lucide-react'
import { useAdminAuditLog, type AuditLogFilters, type AuditLogEntry } from '@/hooks/useAuditLog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Tipos de ação conhecidos para o filtro
// ---------------------------------------------------------------------------

const KNOWN_ACTIONS = [
  'approve_instructor',
  'reject_instructor',
  'ban_user',
  'unban_user',
  'suspend_user',
  'approve_mp_account',
  'process_payout',
  'create_promo_code',
  'update_promo_code',
] as const

const TARGET_TYPES = ['instructor', 'user', 'payout', 'promo_code'] as const

// ---------------------------------------------------------------------------
// JSON viewer expansível
// ---------------------------------------------------------------------------

function JsonViewer({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false)
  const keys = Object.keys(data)

  if (keys.length === 0) {
    return <span className="text-gray-400 text-xs">—</span>
  }

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-brand-600 hover:text-brand-700"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {keys.length} campo{keys.length > 1 ? 's' : ''}
      </button>
      {open && (
        <pre className="mt-1 bg-gray-100 dark:bg-gray-800 rounded p-2 text-[10px] font-mono overflow-x-auto max-w-xs whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row da tabela
// ---------------------------------------------------------------------------

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const date = new Date(entry.created_at)

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {format(date, "dd/MM/yy HH:mm:ss", { locale: ptBR })}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
        {entry.admin_name ?? entry.performed_by?.slice(0, 8) ?? '—'}
      </td>
      <td className="px-4 py-3">
        <Badge
          variant="secondary"
          className={cn(
            'font-mono text-[10px]',
            entry.action.includes('ban') || entry.action.includes('reject')
              ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              : entry.action.includes('approve')
                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
          )}
        >
          {entry.action}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">
        {entry.target_type ?? '—'}
      </td>
      <td className="px-4 py-3 text-xs text-gray-400 font-mono">
        {entry.target_id ? entry.target_id.slice(0, 8) + '…' : '—'}
      </td>
      <td className="px-4 py-3">
        <JsonViewer data={entry.details} />
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50

export default function AdminAuditLog() {
  const [page, setPage]           = useState(0)
  const [filters, setFilters]     = useState<AuditLogFilters>({})
  const [draftAction, setDraft]   = useState('')
  const [draftType, setDraftType] = useState('')
  const [dateFrom, setDateFrom]   = useState('')
  const [dateTo, setDateTo]       = useState('')

  const { data, isLoading } = useAdminAuditLog({
    ...filters,
    page,
    pageSize: PAGE_SIZE,
  })

  const rows  = data?.rows ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const applyFilters = () => {
    setPage(0)
    setFilters({
      action:     draftAction  || undefined,
      targetType: draftType    || undefined,
      from:       dateFrom ? new Date(dateFrom).toISOString() : undefined,
      to:         dateTo   ? new Date(dateTo).toISOString()   : undefined,
    })
  }

  const clearFilters = () => {
    setPage(0)
    setDraft('')
    setDraftType('')
    setDateFrom('')
    setDateTo('')
    setFilters({})
  }

  const hasFilters = draftAction || draftType || dateFrom || dateTo

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Log de Auditoria</h1>
        <p className="text-sm text-gray-500 mt-1">Histórico de ações administrativas no sistema.</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Ação</Label>
              <Select
                value={draftAction}
                onValueChange={setDraft}
              >
                <SelectTrigger className="h-9" data-testid="audit-filter-action">
                  <SelectValue placeholder="Todas as ações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas</SelectItem>
                  {KNOWN_ACTIONS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Tipo de alvo</Label>
              <Select
                value={draftType}
                onValueChange={setDraftType}
              >
                <SelectTrigger className="h-9" data-testid="audit-filter-type">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  {TARGET_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">De</Label>
              <Input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
                data-testid="audit-filter-from"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Até</Label>
              <Input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
                data-testid="audit-filter-to"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button size="sm" onClick={applyFilters} className="gap-2" data-testid="audit-apply-btn">
              <Search className="h-3.5 w-3.5" />
              Filtrar
            </Button>
            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="gap-2 text-gray-500">
                <X className="h-3.5 w-3.5" />
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-brand-600" />
            Registros
            {!isLoading && (
              <Badge variant="secondary" className="ml-auto">{total}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhum registro encontrado.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Data/hora</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Admin</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Ação</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Tipo</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">ID alvo</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {rows.map((entry) => (
                      <AuditRow key={entry.id} entry={entry} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-500">
                  Página {page + 1} de {totalPages} ({total} registros)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    data-testid="audit-next-page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
